import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLists } from '../contexts/ListsContext';
import { useAuth } from '../contexts/AuthContext';
import { Users, Gift, Edit2, Trash2, Share2, LogOut, Plus, Cake, Heart, GraduationCap, Baby, Calendar as CalendarIcon } from 'lucide-react';
import { deleteList, leaveList, getListWishCounts } from '../firebase/database';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmModal from '../components/ConfirmModal';
import ShareListModal from '../components/ShareListModal';
import toast from 'react-hot-toast';
import { useTranslation } from '../hooks/useTranslation';

const Dashboard = () => {
  const { lists, loading, setLists } = useLists();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Function to get the appropriate icon for list occasion
  const getOccasionIcon = (occasion) => {
    switch (occasion) {
      case 'Christmas':
        return <Gift className="h-4 w-4 text-red-500" />;
      case 'Birthday':
        return <Cake className="h-4 w-4 text-pink-500" />;
      case 'Wedding':
        return <Heart className="h-4 w-4 text-rose-500" />;
      case 'Anniversary':
        return <Heart className="h-4 w-4 text-purple-500" />;
      case 'Graduation':
        return <GraduationCap className="h-4 w-4 text-blue-500" />;
      case 'Baby Shower':
        return <Baby className="h-4 w-4 text-yellow-500" />;
      default:
        return <CalendarIcon className="h-4 w-4 text-gray-500" />;
    }
  };
  const [deletingListId, setDeletingListId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [listToDelete, setListToDelete] = useState(null);
  const [leavingListId, setLeavingListId] = useState(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [listToLeave, setListToLeave] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [listToShare, setListToShare] = useState(null);
  const [wishCounts, setWishCounts] = useState({});

  const handleDeleteClick = (list) => {
    setListToDelete(list);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!listToDelete) return;

    setDeletingListId(listToDelete.id);
    
    try {
      const result = await deleteList(listToDelete.id);
      if (result.success) {
        setLists(prev => prev.filter(list => list.id !== listToDelete.id));
        toast.success(t('dashboard.listDeletedSuccess'));
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error(t('dashboard.failedToDeleteList'));
    }
    
    setDeletingListId(null);
    setShowDeleteModal(false);
    setListToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setListToDelete(null);
  };

  const handleLeaveClick = (list) => {
    setListToLeave(list);
    setShowLeaveModal(true);
  };

  const handleShareClick = (list) => {
    setListToShare(list);
    setShowShareModal(true);
  };

  const handleShareModalClose = useCallback(() => {
    setShowShareModal(false);
  }, []);

  const fetchWishCounts = useCallback(async () => {
    if (lists.length === 0) return;
    
    const counts = {};
    const promises = lists.map(async (list) => {
      try {
        const result = await getListWishCounts(list.id);
        if (result.success) {
          counts[list.id] = result.data;
        }
      } catch (error) {
        console.error(`Error fetching wish counts for list ${list.id}:`, error);
      }
    });
    
    await Promise.all(promises);
    setWishCounts(counts);
  }, [lists]);

  // Close share modal when user logs out
  useEffect(() => {
    if (!user && showShareModal) {
      setShowShareModal(false);
    }
  }, [user, showShareModal]);

  // Fetch wish counts when lists change
  useEffect(() => {
    if (lists.length > 0) {
      fetchWishCounts();
    }
  }, [lists, fetchWishCounts]);

  const handleLeaveConfirm = async () => {
    if (!listToLeave || !user) return;

    setLeavingListId(listToLeave.id);
    
    try {
      const result = await leaveList(listToLeave.id, user.uid);
      if (result.success) {
        setLists(prev => prev.filter(list => list.id !== listToLeave.id));
        toast.success(t('dashboard.leftListSuccess'));
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error(t('dashboard.failedToLeaveList'));
    }
    
    setLeavingListId(null);
    setShowLeaveModal(false);
    setListToLeave(null);
  };

  const handleLeaveCancel = () => {
    setShowLeaveModal(false);
    setListToLeave(null);
  };

  const isListOwner = (list) => list.createdBy === user?.uid;

  if (loading) {
    return <LoadingSpinner text={t('dashboard.loadingLists')} />;
  }

  return (
    <div className="max-w-6xl mx-auto pt-6 sm:pt-8 md:pt-16 lg:pt-24 pb-6 sm:pb-8">
      <div className="mb-6 sm:mb-12 px-2 sm:px-0">
        <h1 className="text-xl sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-4 break-words">
          {t('dashboard.welcome')}, {user?.displayName?.split(' ')[0] || user?.email?.split('@')[0] || t('common.user')}!
        </h1>
        <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
          {t('dashboard.manageLists')}
        </p>
        
        {/* New List Button */}
        <Link 
          to="/create-list" 
          className="inline-flex items-center px-4 py-2 sm:px-6 sm:py-4 bg-blue-600 text-white text-sm sm:text-lg font-semibold rounded-lg sm:rounded-xl hover:bg-blue-700 transition-colors duration-150 shadow-md hover:shadow-lg"
        >
          <Plus className="w-4 h-4 sm:w-6 sm:h-6 mr-2 sm:mr-3" />
          {t('dashboard.createNewList')}
        </Link>
      </div>

      <div className="mb-4 sm:mb-8 px-2 sm:px-0">
        <h2 className="text-base sm:text-xl font-semibold text-gray-900">{t('dashboard.yourLists')}</h2>
      </div>

      {lists.length === 0 ? (
        <div className="px-2 sm:px-0">
          <div className="flex items-center text-gray-600 mt-2">
            <Gift className="h-5 w-5 mr-2 text-gray-400" />
            <span>{t('dashboard.noLists')}</span>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 md:gap-8 px-2 sm:px-0">
          {lists.map((list) => {
            const counts = wishCounts[list.id] || { total: 0, bought: 0, planned: 0, isComplete: false };
            return (
            <div key={list.id} className={`group relative h-64 sm:h-72 md:h-80 lg:h-96 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-200 ${counts.isComplete ? 'ring-2 ring-green-200 bg-green-50' : 'hover:border-gray-300'} touch-manipulation`}>
              {/* Dedicated Header Area - Top 20% of card */}
              <div className="absolute top-0 left-0 right-0 h-20 flex items-start justify-between p-4">
                {/* List Type Indicator - Top Left */}
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1.5 bg-gray-50 px-2 py-1 rounded-lg border border-gray-200">
                    {list.occasion ? getOccasionIcon(list.occasion) : <Gift className="h-4 w-4 text-gray-500" />}
                    <span className="text-xs text-gray-600 font-medium">
                      {list.occasion ? t(`createList.occasions.${list.occasion}`) : t('dashboard.wishList')}
                    </span>
                  </div>
                </div>

                {/* Action Buttons - Top Right */}
                <div className="flex items-center space-x-2">
                  {/* Share button - show for all lists */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleShareClick(list);
                    }}
                    className="p-2 text-gray-400 hover:text-blue-600 active:text-blue-700 hover:bg-blue-50 active:bg-blue-100 rounded-lg transition-colors duration-150 touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
                    title={t('dashboard.shareList')}
                  >
                    <Share2 className="h-4 w-4" />
                  </button>

                  {/* Edit and Delete buttons - only show for list owner */}
                  {isListOwner(list) && (
                    <>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/edit-list/${list.id}`);
                        }}
                        className="p-2 text-gray-400 hover:text-primary-600 active:text-primary-700 hover:bg-primary-50 active:bg-primary-100 rounded-lg transition-colors duration-150 touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
                        title={t('dashboard.editList')}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteClick(list);
                        }}
                        disabled={deletingListId === list.id}
                        className="p-2 text-gray-400 hover:text-red-600 active:text-red-700 hover:bg-red-50 active:bg-red-100 rounded-lg transition-colors duration-150 disabled:opacity-50 touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
                        title={t('dashboard.deleteList')}
                      >
                        {deletingListId === list.id ? (
                          <div className="h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </>
                  )}
                  
                  {/* Leave button - show for shared lists that user doesn't own */}
                  {!isListOwner(list) && list.isGroup && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleLeaveClick(list);
                      }}
                      disabled={leavingListId === list.id}
                      className="p-2 text-gray-400 hover:text-orange-600 active:text-orange-700 hover:bg-orange-50 active:bg-orange-100 rounded-lg transition-colors duration-150 disabled:opacity-50 touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
                      title={t('dashboard.leaveList')}
                    >
                      {leavingListId === list.id ? (
                        <div className="h-4 w-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <LogOut className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Dedicated Content Area - Middle 60% of card */}
              <div className="absolute top-20 left-0 right-0 h-40 sm:h-56 md:h-64 lg:h-72 flex flex-col p-4 border-t border-gray-100">
                <Link to={`/list/${list.id}`} className="flex-1 block flex flex-col">
                  {/* Headline Area - Single Line */}
                  <div className="h-10 flex items-center mb-3">
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors truncate w-full">
                      {list.name}
                    </h3>
                  </div>

                  {/* Description Area - Max Three Lines */}
                  <div className="h-12 sm:h-20 md:h-24 flex items-start mb-4">
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed line-clamp-2 sm:line-clamp-3 md:line-clamp-4 w-full">
                      {list.description || t('dashboard.noDescription')}
                    </p>
                  </div>

                  {/* Spacer to push content up */}
                  <div className="flex-1"></div>
                </Link>
              </div>

              {/* Single Line with ALL Info - Bottom 20% of card */}
              <div className="absolute bottom-0 left-0 right-0 h-16 sm:h-20 flex items-center justify-between p-3 sm:p-4 border-t border-gray-100">
                {/* Evenly distributed info across the full width */}
                <div className="flex items-center justify-between w-full text-xs sm:text-sm text-gray-600 gap-2">
                  {/* List Type */}
                  <div className="flex items-center space-x-1.5">
                    {list.isGroup ? (
                      <>
                        <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                        <span className="text-gray-500 font-medium">{t('dashboard.groupList')}</span>
                      </>
                    ) : (
                      <>
                        <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                        <span className="text-gray-500 font-medium">{t('dashboard.personalList')}</span>
                      </>
                    )}
                  </div>
                  
                  {/* Wishes count */}
                  <div className="flex items-center space-x-1.5">
                    <Gift className="h-4 w-4 text-gray-400" />
                    <span>{counts.total}</span>
                  </div>
                  
                  {/* Members count */}
                  <div className="flex items-center space-x-1.5">
                    <Users className="h-4 w-4 text-gray-400" />
                    <span>{list.members?.length || 1}</span>
                  </div>

                  {/* Bought/Planned stats */}
                  <div className="flex items-center space-x-3">
                    {counts.total > 0 ? (
                      <>
                        {/* Bought Stats */}
                        <div className="flex items-center space-x-1.5">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-green-700 font-medium">
                            {counts.bought}/{counts.total}
                          </span>
                        </div>
                        {/* Planned Stats */}
                        <div className="flex items-center space-x-1.5">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="text-blue-700 font-medium">
                            {counts.planned}/{counts.total}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center space-x-1.5 text-gray-400">
                        <Gift className="h-4 w-4" />
                        <span className="text-xs">{t('dashboard.noWishesAddedYet')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>
            );
          })}
        </div>
      )}


      {/* Custom Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('modals.deleteConfirm')}
        message={`${t('dashboard.deleteListConfirm')} "${listToDelete?.name}"${t('dashboard.deleteListConfirmEnd')}`}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
      />

      {/* Custom Leave Confirmation Modal */}
      <ConfirmModal
        isOpen={showLeaveModal}
        onClose={handleLeaveCancel}
        onConfirm={handleLeaveConfirm}
        title={t('modals.leaveConfirm')}
        message={`${t('dashboard.leaveListConfirm')} "${listToLeave?.name}"${t('dashboard.leaveListConfirmEnd')}`}
        confirmText={t('dashboard.leaveList')}
        cancelText={t('common.cancel')}
        type="warning"
      />

      {/* Share List Modal */}
      {showShareModal && listToShare && (
        <ShareListModal
          isOpen={showShareModal}
          list={listToShare}
          onClose={handleShareModalClose}
        />
      )}
    </div>
  );
};

export default Dashboard;
